import React, { useMemo } from "react";

import {
  GAP_TYPES,
  NA_AGE_FILTERS,
  RISK_TYPES,
  SETUP_LABELS,
  STATUS_LABELS,
  buildGapItems,
  buildRiskItems,
  buildRiskModel,
  gapAgeCompare,
  gapPriorityCompare,
  gapTypeLabel,
  gapWhoCovers,
  naAgeMatch,
  naSharedMatch,
  riskShortLabel,
  riskTypeLabel,
  type AttentionItem,
  type CoverageAccount,
  type CoverageFilters,
  type CoverageRow,
  type GapItem,
  type GapType,
  type NaView,
  type RiskItem,
  type RiskType,
} from "./coverageModel";
import DropdownSelector from "../../Components/DropdownSelector";
import TabsComponent from "../../Components/Tab";
import type {
  NaGapsState,
  NaRisksState,
} from "../../Store/Reducers/CoverageSlice";
import { useTeamViewsPrivilegedAccess } from "../Relationship/team-views/teamViewsAccess";

function plural(n: number, singular: string, pluralForm?: string) {
  return n + " " + (n === 1 ? singular : pluralForm || singular + "s");
}

function KeyBadge(props: { badge: string | null | undefined }) {
  if (!props.badge) return null;
  return (
    <span
      className="ml-1 rounded bg-violet-100 px-1 text-[9px] font-semibold uppercase text-violet-800"
      title={
        props.badge === "Exec"
          ? "Key role (Executive, MEDDPICC off)"
          : "Key role (MEDDPICC Buying Committee)"
      }
    >
      {props.badge}
    </span>
  );
}

function openForTitle(item: GapItem): string | undefined {
  if (item.ageDays == null) return undefined;
  if (item.gapType === "lifecycle") return "Dormant since";
  if (item.gapType === "setup") return "Account added";
  if (item.gapType === "pending") return "Handoff";
  return "Contact added";
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function NaCtaLink(props: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  className?: string;
  disabled?: boolean;
}) {
  const disabled = Boolean(props.disabled);
  return (
    <button
      type="button"
      disabled={disabled}
      title={props.title || (disabled ? "Need's Exec Ops Access" : undefined)}
      aria-disabled={disabled || undefined}
      className={
        "whitespace-nowrap text-[11px] leading-snug " +
        (disabled
          ? "cursor-not-allowed text-gray-400 "
          : "text-[#974ae4] hover:underline ") +
        (props.className || "")
      }
      onClick={function (e) {
        if (disabled) {
          e.preventDefault();
          return;
        }
        props.onClick(e);
      }}
    >
      {props.children}
    </button>
  );
}

function Chip(props: {
  label: string;
  count: number;
  active: boolean;
  disabled?: boolean;
  sub?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      aria-pressed={props.active}
      className={
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition " +
        (props.active
          ? "border-violet-400 bg-violet-50 text-violet-900"
          : props.disabled
            ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300"
            : "border-gray-200 bg-white text-gray-700 hover:border-violet-200")
      }
      onClick={props.onClick}
    >
      {props.label} <b>{props.count}</b>
      {props.sub ? (
        <small className="font-normal text-gray-500">{props.sub}</small>
      ) : null}
    </button>
  );
}

/**
 * What Next step can show (same order as gapNextStep):
 * - setup → Add contacts / Set up account team
 * - lifecycle → Pick a successor (disabled + “Need's Exec Ops Access” tooltip if unprivileged)
 * - pending → Confirm handoff
 * - needsTeam → Needs an account team first
 * - unowned contact → Add an owner
 */
function gapHasNextStep(item: GapItem): boolean {
  if (item.gapType === "setup" && item.account_id) return true;
  if (item.gapType === "lifecycle" && item.former_owner_id) return true;
  if (item.gapType === "pending" && item.account_id) return true;
  if (item.needsTeam && item.account_id) return true;
  if (item.account_id && item.contact_id) return true;
  return false;
}

function gapNextStep(
  item: GapItem,
  onSetup: (accountId: string) => void,
  onSuccessor: (ownerId: string) => void,
  onConfirm: (accountId: string) => void,
  onAddOwner: (accountId: string, contactId: string) => void,
  canManageTransfer: boolean
) {
  if (item.gapType === "setup" && item.account_id) {
    return (
      <NaCtaLink
        onClick={function () {
          onSetup(item.account_id!);
        }}
      >
        {item.condition_key === "no-contacts"
          ? "Add contacts →"
          : "Set up account team →"}
      </NaCtaLink>
    );
  }
  // Same privilege gate as Team Views Reassign / Owner Portfolio departure impact.
  // Always show the CTA; unprivileged users get a disabled link + tooltip.
  if (item.gapType === "lifecycle" && item.former_owner_id) {
    return (
      <NaCtaLink
        disabled={!canManageTransfer}
        onClick={function () {
          onSuccessor(item.former_owner_id!);
        }}
      >
        Pick a successor →
      </NaCtaLink>
    );
  }
  if (item.gapType === "pending" && item.account_id) {
    return (
      <NaCtaLink
        onClick={function () {
          onConfirm(item.account_id!);
        }}
      >
        Confirm handoff →
      </NaCtaLink>
    );
  }
  if (item.needsTeam && item.account_id) {
    return (
      <button
        type="button"
        className="text-xs font-medium text-amber-800 hover:underline"
        title="No account team — open Account Workspace to set up the team"
        onClick={function () {
          onSetup(item.account_id!);
        }}
      >
        Needs an account team first
      </button>
    );
  }
  if (item.account_id && item.contact_id) {
    return (
      <NaCtaLink
        onClick={function () {
          onAddOwner(item.account_id!, item.contact_id!);
        }}
      >
        Add an owner →
      </NaCtaLink>
    );
  }
  return null;
}

/** Shared so every Gaps table lines up. Account grouping drops the account column. */
const GAP_COL_WIDTHS = ["12%", "19%", "13%", "21%", "11%", "9%", "15%"];
const GAP_COL_WIDTHS_NO_ACCOUNT = ["22%", "15%", "24%", "12%", "10%", "17%"];

function GapTable(props: {
  items: GapItem[];
  /** Keep the account column, but leave the cell empty (grouped child rows). */
  hideAccount?: boolean;
  /** Drop the account column. Used when the account name is the card title. */
  omitAccount?: boolean;
  compact?: boolean;
  noAction?: boolean;
  resolvedRows?: CoverageRow[];
  onViewHistory?: (accountId: string) => void;
  onAccount: (id: string) => void;
  onSetup: (accountId: string) => void;
  onSuccessor: (ownerId: string) => void;
  onConfirm: (accountId: string) => void;
  onAddOwner: (accountId: string, contactId: string) => void;
}) {
  const { items, hideAccount, omitAccount, compact, noAction } = props;
  const resolvedRows = props.resolvedRows || [];
  const canManageTransfer = useTeamViewsPrivilegedAccess();
  const showNextStep = !noAction && items.some(gapHasNextStep);
  if (!items.length && !resolvedRows.length) return null;
  const widths = omitAccount ? GAP_COL_WIDTHS_NO_ACCOUNT : GAP_COL_WIDTHS;
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse text-left text-xs text-gray-800 [&_td]:break-words [&_th]:break-words">
        <colgroup>
          {widths.map(function (width, index) {
            return <col key={index} style={{ width: width }} />;
          })}
        </colgroup>
        <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            {!omitAccount ? <th className="px-3 py-2">Account</th> : null}
            <th className="px-3 py-2">Contact / role</th>
            <th className="px-3 py-2">Gap</th>
            <th className="px-3 py-2">Who covers it now</th>
            <th className="px-3 py-2">RL / goal</th>
            <th className="px-3 py-2">Open for</th>
            <th className="px-3 py-2">Next step</th>
          </tr>
        </thead>
        <tbody>
          {items.map(function (item) {
            return (
              <tr
                key={item.id}
                className={
                  "border-t border-gray-100 " +
                  (item.isKey ? "bg-amber-50/40" : "")
                }
              >
                {!omitAccount ? (
                  <td className="px-3 py-2">
                    {hideAccount ? null : item.account_id ? (
                      <button
                        type="button"
                        className="font-medium text-[#974ae4] hover:underline"
                        onClick={function () {
                          props.onAccount(item.account_id!);
                        }}
                      >
                        {item.account_name}
                      </button>
                    ) : (
                      item.account_name
                    )}
                  </td>
                ) : null}
                <td className="px-3 py-2">
                  {item.gapType === "setup" ? (
                    item.foldedCount ? (
                      <>
                        {plural(
                          item.foldedCount,
                          item.foldedKeyCount === item.foldedCount
                            ? "key contact"
                            : "contact"
                        )}
                        , no owners yet
                        <div className="text-[10px] text-gray-500">
                          Listed under Unowned
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-500">No contacts yet</span>
                    )
                  ) : (
                    <>
                      {item.contact_name}
                      <KeyBadge badge={item.keyBadge} />
                      <div className="text-[10px] text-gray-500">
                        {item.contact_role_label || "Unclassified"}
                      </div>
                    </>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-800">
                    {compact
                      ? item.gapType === "setup"
                        ? SETUP_LABELS[item.condition_key] || item.condition_key
                        : item.gapType === "lifecycle"
                          ? item.status === "open-orphaned"
                            ? "Orphaned"
                            : "Covered elsewhere"
                          : item.gapType === "pending"
                            ? "Awaiting confirmation"
                            : item.isKey
                              ? "Key role"
                              : "No owner"
                      : gapTypeLabel(item)}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-800">
                  {gapWhoCovers(item)}
                </td>
                <td className="px-3 py-2">
                  {item.gapType === "setup" || item.gapType === "unowned" ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      {item.rl != null ? "RL " + item.rl : "Not assessed"}
                      {item.goal_target_rl != null
                        ? " · goal RL" + item.goal_target_rl
                        : ""}
                      {item.gapType === "lifecycle" ? (
                        <div className="text-[10px] text-gray-500">
                          {item.has_history_context
                            ? "RL history kept"
                            : "No RL / goal history"}
                        </div>
                      ) : null}
                    </>
                  )}
                </td>
                <td className="px-3 py-2" title={openForTitle(item)}>
                  {item.ageDays != null
                    ? plural(item.ageDays, "day")
                    : "Unknown"}
                </td>
                <td className="px-3 py-2">
                  {showNextStep
                    ? gapNextStep(
                        item,
                        props.onSetup,
                        props.onSuccessor,
                        props.onConfirm,
                        props.onAddOwner,
                        canManageTransfer
                      )
                    : null}
                </td>
              </tr>
            );
          })}
          {resolvedRows.map(function (row) {
            return (
              <tr key={row.id} className="border-t border-gray-100">
                {!omitAccount ? (
                  <td className="px-3 py-2">{row.account_name}</td>
                ) : null}
                <td className="px-3 py-2">
                  {row.contact_name}
                  <div className="text-[10px] text-gray-500">
                    {row.contact_role_label || "Unclassified"}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {STATUS_LABELS[row.status] || row.status}
                </td>
                <td className="px-3 py-2 text-gray-800">
                  {row.current_owner_names?.length
                    ? "Now " + row.current_owner_names.join(", ")
                    : "—"}
                </td>
                <td className="px-3 py-2">
                  {row.rl != null ? "RL " + row.rl : "Not assessed"}
                </td>
                <td className="px-3 py-2">—</td>
                <td className="px-3 py-2">
                  <NaCtaLink
                    onClick={function () {
                      props.onViewHistory?.(row.account_id);
                    }}
                  >
                    View history →
                  </NaCtaLink>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UnownedGroups(props: {
  items: GapItem[];
  onAccount: (id: string) => void;
  onSetup: (accountId: string) => void;
  onSuccessor: (ownerId: string) => void;
  onConfirm: (accountId: string) => void;
  onAddOwner: (accountId: string, contactId: string) => void;
}) {
  const byAcct = new Map<string, GapItem[]>();
  props.items.forEach(function (i) {
    if (!i.account_id) return;
    const list = byAcct.get(i.account_id) || [];
    list.push(i);
    byAcct.set(i.account_id, list);
  });
  const groups = Array.from(byAcct.entries())
    .map(function ([accountId, items]) {
      return {
        accountId: accountId,
        items: items.slice().sort(gapPriorityCompare),
        keyN: items.filter(i => i.isKey).length,
        oldest: items.reduce(function (a, i) {
          return (i.ageDays ?? -1) > (a.ageDays ?? -1) ? i : a;
        }, items[0]),
      };
    })
    .sort(function (a, b) {
      return (
        Number(b.keyN > 0) - Number(a.keyN > 0) ||
        (b.oldest.ageDays ?? -1) - (a.oldest.ageDays ?? -1) ||
        a.items[0].account_name.localeCompare(b.items[0].account_name)
      );
    });

  return (
    <div className="flex flex-col gap-2">
      {groups.map(function (g) {
        const needsTeam = g.items[0].needsTeam;
        return (
          <details
            key={g.accountId}
            open
            className="border-t border-gray-100 bg-white"
            data-unowned-account={g.accountId}
          >
            <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-900 [&::-webkit-details-marker]:hidden">
              <button
                type="button"
                className="text-[#974ae4] hover:underline"
                onClick={function (e) {
                  e.preventDefault();
                  props.onAccount(g.accountId);
                }}
              >
                {g.items[0].account_name}
              </button>
              <span className="text-xs font-normal text-gray-500">
                {plural(g.items.length, "contact")}
                {g.keyN ? " · " + plural(g.keyN, "key role") : ""}
              </span>
              <span className="ml-auto text-xs font-normal text-gray-500">
                {g.oldest.ageDays != null
                  ? plural(g.oldest.ageDays, "day") +
                    (g.items.length > 1 ? " · oldest" : "")
                  : "Unknown"}
              </span>
              {needsTeam ? (
                <button
                  type="button"
                  className="text-xs font-medium text-amber-800 hover:underline"
                  onClick={function (e) {
                    e.preventDefault();
                    props.onSetup(g.accountId);
                  }}
                >
                  Needs an account team first
                </button>
              ) : null}
            </summary>
            <GapTable
              items={g.items}
              hideAccount
              compact
              onAccount={props.onAccount}
              onSetup={props.onSetup}
              onSuccessor={props.onSuccessor}
              onConfirm={props.onConfirm}
              onAddOwner={props.onAddOwner}
            />
          </details>
        );
      })}
    </div>
  );
}

function LifecycleGroups(props: {
  items: GapItem[];
  rows: CoverageRow[];
  onAccount: (id: string) => void;
  onSetup: (accountId: string) => void;
  onSuccessor: (ownerId: string) => void;
  onConfirm: (accountId: string) => void;
  onAddOwner: (accountId: string, contactId: string) => void;
  onViewHistory: (accountId: string) => void;
}) {
  const canManageTransfer = useTeamViewsPrivilegedAccess();
  const byOwner = new Map<string, GapItem[]>();
  props.items.forEach(function (item) {
    const id = item.former_owner_id || "unknown";
    const list = byOwner.get(id) || [];
    list.push(item);
    byOwner.set(id, list);
  });

  return (
    <div className="flex flex-col gap-2">
      {Array.from(byOwner.entries()).map(function ([ownerId, open]) {
        const records = props.rows.filter(function (row) {
          return row.owner_id === ownerId && row.lifecycle_state === "dormant";
        });
        const transferred = records.filter(function (row) {
          return row.status === "transferred";
        }).length;
        const dismissed = records.filter(function (row) {
          return row.status === "dismissed";
        }).length;
        const departed = records
          .map(function (row) {
            return row.dormant_at || "";
          })
          .filter(Boolean)
          .sort()
          .pop();
        const resolvedText = [
          transferred ? transferred + " transferred" : "",
          dismissed ? dismissed + " dismissed" : "",
        ]
          .filter(Boolean)
          .join(", ");
        const name = open[0].former_owner_name || "Former owner";
        const openIds = new Set(
          open.map(function (item) {
            return item.id;
          })
        );
        const resolved = records.filter(function (row) {
          return (
            (row.status === "transferred" || row.status === "dismissed") &&
            !openIds.has(row.id)
          );
        });
        return (
          <details
            key={ownerId}
            open
            className="border-t border-gray-100 bg-white"
          >
            <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 px-3 py-2 text-sm [&::-webkit-details-marker]:hidden">
              <strong className="text-gray-900">
                {name}
                {departed ? " · departed " + shortDate(departed) : ""}
              </strong>
              <span className="text-xs font-normal text-gray-500">
                {open.length} open of {records.length || open.length}
                {resolvedText ? " (" + resolvedText + ")" : ""}
              </span>
              <span className="ml-auto">
                {gapNextStep(
                  open[0],
                  props.onSetup,
                  props.onSuccessor,
                  props.onConfirm,
                  props.onAddOwner,
                  canManageTransfer
                )}
              </span>
            </summary>
            <GapTable
              items={open}
              compact
              noAction
              resolvedRows={resolved}
              onViewHistory={props.onViewHistory}
              onAccount={props.onAccount}
              onSetup={props.onSetup}
              onSuccessor={props.onSuccessor}
              onConfirm={props.onConfirm}
              onAddOwner={props.onAddOwner}
            />
          </details>
        );
      })}
    </div>
  );
}

function GapsView(props: {
  gapsAll: GapItem[];
  gapsShown: GapItem[];
  rows: CoverageRow[];
  naGaps: NaGapsState;
  riskPatterns: number;
  accountScope: string;
  onSetGaps: (key: keyof NaGapsState, value: string) => void;
  onSwitchToRisks: () => void;
  onAccount: (id: string) => void;
  onSetup: (accountId: string) => void;
  onSuccessor: (ownerId: string) => void;
  onConfirm: (accountId: string) => void;
  onAddOwner: (accountId: string, contactId: string) => void;
  onViewHistory: (accountId: string) => void;
}) {
  const { gapsAll, gapsShown, naGaps } = props;
  const tab = naGaps.type;
  const lifecycleMatch = function (i: GapItem) {
    if (naGaps.lifecycle === "orphaned")
      return i.gapType !== "lifecycle" || i.status === "open-orphaned";
    if (naGaps.lifecycle === "covered")
      return i.gapType !== "lifecycle" || i.status === "open-covered";
    return true;
  };
  const forAges = gapsAll.filter(function (i) {
    if (!lifecycleMatch(i)) return false;
    return tab === "all" || i.gapType === tab;
  });
  const forTypes = gapsAll.filter(function (i) {
    return lifecycleMatch(i) && naAgeMatch(i, naGaps.age);
  });

  const sections = GAP_TYPES.map(function (t) {
    const list = forTypes.filter(i => i.gapType === t.key);
    const keyN = t.key === "unowned" ? list.filter(i => i.isKey).length : 0;
    return { ...t, list: list, keyN: keyN };
  });

  let body: React.ReactNode;
  if (!gapsShown.length) {
    body = (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
        No gaps{props.accountScope}
        {tab !== "all" || naGaps.age !== "all" ? " of this type" : ""}.
        {props.riskPatterns ? (
          <>
            {" "}
            <button
              type="button"
              className="font-medium text-[#974ae4] hover:underline"
              onClick={props.onSwitchToRisks}
            >
              {plural(props.riskPatterns, "risk pattern")} → Risks
            </button>
          </>
        ) : null}
      </div>
    );
  } else if (naGaps.groupBy === "account") {
    const byAcct = new Map<string, GapItem[]>();
    gapsShown.forEach(function (i) {
      const id = i.account_id || "portfolio";
      const list = byAcct.get(id) || [];
      list.push(i);
      byAcct.set(id, list);
    });
    body = (
      <div className="flex flex-col gap-3">
        {Array.from(byAcct.entries()).map(function ([id, list]) {
          const sorted =
            naGaps.sort === "oldest"
              ? list.slice().sort(gapAgeCompare)
              : list.slice().sort(gapPriorityCompare);
          return (
            <div
              key={id}
              className="rounded-lg border border-gray-200 bg-white"
            >
              <div className="border-b border-gray-100 px-3 py-2 text-sm text-gray-900">
                <button
                  type="button"
                  className="font-semibold text-[#974ae4] hover:underline"
                  onClick={function () {
                    if (id !== "portfolio") props.onAccount(id);
                  }}
                >
                  {list[0].account_name}
                </button>
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {list.length}
                </span>
                <div className="text-[11px] font-normal text-gray-500">
                  {GAP_TYPES.map(function (t) {
                    const n = list.filter(function (i) {
                      return i.gapType === t.key;
                    }).length;
                    return n ? n + " " + t.label.toLowerCase() : "";
                  })
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <GapTable
                items={sorted}
                omitAccount
                onAccount={props.onAccount}
                onSetup={props.onSetup}
                onSuccessor={props.onSuccessor}
                onConfirm={props.onConfirm}
                onAddOwner={props.onAddOwner}
              />
            </div>
          );
        })}
      </div>
    );
  } else if (naGaps.sort === "oldest") {
    body = (
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-900">
          All gaps, oldest first
          <span className="ml-2 text-xs font-normal text-gray-500">
            {gapsShown.length}
          </span>
          <div className="text-[11px] font-normal text-gray-500">
            Ranked by Open for; key roles first when ages tie
          </div>
        </div>
        <GapTable
          items={gapsShown.slice().sort(gapAgeCompare)}
          onAccount={props.onAccount}
          onSetup={props.onSetup}
          onSuccessor={props.onSuccessor}
          onConfirm={props.onConfirm}
          onAddOwner={props.onAddOwner}
        />
      </div>
    );
  } else {
    const of = function (t: GapType) {
      return gapsShown.filter(i => i.gapType === t);
    };
    body = (
      <div className="flex flex-col gap-3">
        {GAP_TYPES.map(function (t) {
          const list = of(t.key);
          if (!list.length) return null;
          const bare = tab === t.key;
          return (
            <div
              key={t.key}
              className="rounded-lg border border-gray-200 bg-white"
              data-gap-section={t.key}
            >
              {!bare ? (
                <div className="border-b border-gray-100 px-3 py-2 text-sm text-gray-900">
                  <strong>{t.title}</strong>
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {list.length}
                  </span>
                  <div className="text-[11px] font-normal text-gray-500">
                    {t.key === "unowned" &&
                    list.filter(function (i) {
                      return i.isKey;
                    }).length
                      ? plural(
                          list.filter(function (i) {
                            return i.isKey;
                          }).length,
                          "key role"
                        ) +
                        " across " +
                        plural(
                          new Set(
                            list
                              .filter(function (i) {
                                return i.isKey;
                              })
                              .map(function (i) {
                                return i.account_id;
                              })
                          ).size,
                          "account"
                        ) +
                        " · " +
                        t.sub
                      : t.sub}
                  </div>
                </div>
              ) : null}
              {t.key === "lifecycle" ? (
                <LifecycleGroups
                  items={list}
                  rows={props.rows}
                  onAccount={props.onAccount}
                  onSetup={props.onSetup}
                  onSuccessor={props.onSuccessor}
                  onConfirm={props.onConfirm}
                  onAddOwner={props.onAddOwner}
                  onViewHistory={props.onViewHistory}
                />
              ) : t.key === "unowned" ? (
                <div className="p-2">
                  <UnownedGroups
                    items={list}
                    onAccount={props.onAccount}
                    onSetup={props.onSetup}
                    onSuccessor={props.onSuccessor}
                    onConfirm={props.onConfirm}
                    onAddOwner={props.onAddOwner}
                  />
                </div>
              ) : (
                <GapTable
                  items={list}
                  compact
                  onAccount={props.onAccount}
                  onSetup={props.onSetup}
                  onSuccessor={props.onSuccessor}
                  onConfirm={props.onConfirm}
                  onAddOwner={props.onAddOwner}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 pb-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip
            label="All"
            count={forTypes.length}
            active={tab === "all"}
            onClick={function () {
              props.onSetGaps("type", "all");
            }}
          />
          {sections.map(function (s) {
            return (
              <Chip
                key={s.key}
                label={s.label}
                count={s.list.length}
                active={tab === s.key}
                disabled={!s.list.length}
                sub={
                  s.key === "unowned" && s.keyN
                    ? "· " + s.keyN + " key role" + (s.keyN === 1 ? "" : "s")
                    : s.key === "lifecycle" && naGaps.lifecycle !== "all"
                      ? "· " +
                        (naGaps.lifecycle === "orphaned"
                          ? "orphaned only"
                          : "covered elsewhere only")
                      : undefined
                }
                onClick={function () {
                  props.onSetGaps("type", s.key);
                }}
              />
            );
          })}
        </div>
        <div className="flex items-center gap-2 border-l border-gray-200 pl-3">
          <span className="text-[11px] font-semibold text-gray-500">
            Open for
          </span>
          <DropdownSelector
            label="Open for"
            hideLabel
            className="na-inline-select"
            selected={
              NA_AGE_FILTERS.some(function (f) {
                return f.key === naGaps.age;
              })
                ? naGaps.age
                : "all"
            }
            options={NA_AGE_FILTERS.map(function (f) {
              const n = forAges.filter(function (i) {
                return naAgeMatch(i, f.key);
              }).length;
              return { value: f.key, label: f.label + " (" + n + ")" };
            })}
            onSelect={function (v) {
              props.onSetGaps("age", v);
            }}
            compact
            triggerStyle="field"
          />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-gray-500">
              Group by
            </span>
            <DropdownSelector
              label="Group by"
              hideLabel
              className="na-inline-select"
              selected={naGaps.groupBy}
              options={[
                { value: "type", label: "Gap type" },
                { value: "account", label: "Account" },
              ]}
              onSelect={function (v) {
                props.onSetGaps("groupBy", v);
              }}
              compact
              triggerStyle="field"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-gray-500">
              Sort
            </span>
            <DropdownSelector
              label="Sort"
              hideLabel
              className="na-inline-select"
              selected={naGaps.sort}
              options={[
                { value: "priority", label: "Priority" },
                { value: "oldest", label: "Oldest first" },
              ]}
              onSelect={function (v) {
                props.onSetGaps("sort", v);
              }}
              compact
              triggerStyle="field"
            />
          </div>
        </div>
      </div>
      {body}
    </div>
  );
}

function RisksGrouped(props: {
  items: RiskItem[];
  groupBy: "owner" | "account";
  onAccount: (id: string) => void;
  onAddOwner: (accountId: string, contactId: string) => void;
  onOpenPortfolio: (ownerId: string, filter?: string | null) => void;
}) {
  const groups = new Map<string, RiskItem[]>();
  props.items.forEach(function (item) {
    const keys =
      props.groupBy === "owner"
        ? item.riskType === "fan-in"
          ? item.current_owner_ids || []
          : item.owner_id
            ? [item.owner_id]
            : []
        : [item.account_id || "portfolio"];
    keys.forEach(function (key) {
      if (!key) return;
      const list = groups.get(key) || [];
      list.push(item);
      groups.set(key, list);
    });
  });
  const order = Array.from(groups.entries()).sort(function (a, b) {
    const types = function (list: RiskItem[]) {
      return new Set(
        list.map(function (i) {
          return i.riskType;
        })
      ).size;
    };
    return types(b[1]) - types(a[1]) || b[1].length - a[1].length;
  });
  if (!order.length) return null;
  return (
    <div className="flex flex-col gap-3">
      {order.map(function ([key, list]) {
        const name =
          props.groupBy === "owner"
            ? list.find(function (i) {
                return i.owner_id === key;
              })?.owner_name ||
              list.find(function (i) {
                return (i.current_owner_ids || []).includes(key);
              })?.current_owner_names?.[
                (
                  list.find(function (i) {
                    return (i.current_owner_ids || []).includes(key);
                  })?.current_owner_ids || []
                ).indexOf(key)
              ] ||
              "Owner"
            : key === "portfolio"
              ? "Portfolio-wide"
              : list[0].account_name;
        const parts = RISK_TYPES.map(function (type) {
          const n = list.filter(function (i) {
            return i.riskType === type;
          }).length;
          return n ? n + " " + riskShortLabel(type).toLowerCase() : "";
        })
          .filter(Boolean)
          .join(" · ");
        const typesN = new Set(
          list.map(function (i) {
            return i.riskType;
          })
        ).size;
        return (
          <div key={key} className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-3 py-2 text-sm text-gray-900">
              <strong>{name}</strong>
              <span className="ml-2 text-xs font-normal text-gray-500">
                {plural(typesN, "risk")}
              </span>
              <div className="text-[11px] font-normal text-gray-500">
                {parts}
              </div>
            </div>
            <RiskRows
              items={list}
              showType
              onAccount={props.onAccount}
              onAddOwner={props.onAddOwner}
              onOpenPortfolio={props.onOpenPortfolio}
            />
          </div>
        );
      })}
    </div>
  );
}

function RiskRows(props: {
  items: RiskItem[];
  showType?: boolean;
  onAccount: (id: string) => void;
  onAddOwner: (accountId: string, contactId: string) => void;
  onOpenPortfolio: (ownerId: string, filter?: string | null) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed text-left text-xs">
        <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            {props.showType ? <th className="px-3 py-2">Risk</th> : null}
            <th className="px-3 py-2">Account</th>
            <th className="px-3 py-2">Contact / owner</th>
            <th className="px-3 py-2">Coverage now</th>
            <th className="px-3 py-2">RL / goal</th>
            <th className="px-3 py-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map(function (i) {
            return (
              <tr
                key={i.id + (props.showType ? "" : "")}
                className={
                  "border-t border-gray-100 " +
                  (i.isKey ? "bg-amber-50/40" : "")
                }
              >
                {props.showType ? (
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-800">
                      {riskShortLabel(i.riskType)}
                    </span>
                  </td>
                ) : null}
                <td className="px-3 py-2">
                  {i.account_id ? (
                    <button
                      type="button"
                      className="font-medium text-[#974ae4] hover:underline"
                      onClick={function () {
                        props.onAccount(i.account_id!);
                      }}
                    >
                      {i.account_name}
                    </button>
                  ) : (
                    <strong>Portfolio-wide</strong>
                  )}
                </td>
                <td className="px-3 py-2">
                  {i.riskType === "capacity" ? (
                    <>
                      <button
                        type="button"
                        className="font-medium text-[#974ae4] hover:underline"
                        onClick={function () {
                          if (i.owner_id)
                            props.onOpenPortfolio(i.owner_id, "over");
                        }}
                      >
                        {i.owner_name}
                      </button>
                      <div className="text-[10px] text-gray-500">
                        {i.owner_role}
                      </div>
                    </>
                  ) : (
                    <>
                      {i.contact_name}
                      <KeyBadge badge={i.keyBadge} />
                      <div className="text-[10px] text-gray-500">
                        {i.contact_role_label}
                      </div>
                    </>
                  )}
                </td>
                <td className="px-3 py-2">
                  {i.riskType === "capacity"
                    ? i.local_overload
                      ? i.local_fan_out +
                        " / " +
                        i.local_capacity +
                        " in this account"
                      : i.global_fan_out +
                        " / " +
                        i.global_capacity +
                        " portfolio-wide"
                    : i.riskType === "single-owner"
                      ? "Only " + (i.owner_name || "one owner")
                      : (i.current_owner_names || []).length +
                        " owners: " +
                        (i.current_owner_names || []).join(", ")}
                </td>
                <td className="px-3 py-2">
                  {i.riskType === "capacity" ? (
                    "—"
                  ) : (
                    <>
                      {i.rl != null ? "RL " + i.rl : "Not assessed"}
                      {i.goal_target_rl != null
                        ? " · goal RL" + i.goal_target_rl
                        : ""}
                    </>
                  )}
                </td>
                <td className="px-3 py-2">
                  {i.riskType === "capacity" && i.owner_id ? (
                    <NaCtaLink
                      onClick={function () {
                        props.onOpenPortfolio(i.owner_id!, "over");
                      }}
                    >
                      Open portfolio →
                    </NaCtaLink>
                  ) : i.account_id && i.contact_id ? (
                    <NaCtaLink
                      onClick={function () {
                        props.onAddOwner(i.account_id!, i.contact_id!);
                      }}
                    >
                      {i.riskType === "single-owner"
                        ? "Add a second owner →"
                        : "Review owners →"}
                    </NaCtaLink>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RisksView(props: {
  risksAll: RiskItem[];
  risksShown: RiskItem[];
  model: ReturnType<typeof buildRiskModel>;
  naRisks: NaRisksState;
  gapCount: number;
  onSetRisks: (key: keyof NaRisksState, value: string) => void;
  onSwitchToGaps: () => void;
  onAccount: (id: string) => void;
  onAddOwner: (accountId: string, contactId: string) => void;
  onOpenPortfolio: (ownerId: string, filter?: string | null) => void;
  onSimulate: (ownerId: string) => void;
  onFilterAccount: (accountId: string | null) => void;
  onSeeOwnerRisks: (ownerId: string) => void;
  selectedAccount: string;
  accountName: string;
  risksBase: RiskItem[];
  rows: CoverageRow[];
}) {
  const { risksAll, model, naRisks } = props;
  const canManageTransfer = useTeamViewsPrivilegedAccess();
  const tab = naRisks.type;

  const tile = function (type: RiskType) {
    const list =
      type === "single-owner"
        ? model.single
        : type === "capacity"
          ? model.capacity
          : model.fan;
    const n =
      type === "capacity"
        ? new Set(list.map(i => i.owner_id).filter(Boolean)).size
        : list.length;
    const unit =
      type === "capacity"
        ? n === 1
          ? "owner over limit"
          : "owners over limit"
        : n === 1
          ? "contact"
          : "contacts";
    const active = tab === type;
    return (
      <button
        key={type}
        type="button"
        disabled={!list.length}
        aria-pressed={active}
        data-risk-tile={type}
        className={
          "flex flex-col gap-1 rounded-lg border bg-white p-3 text-left transition " +
          (active
            ? "border-violet-400 ring-2 ring-violet-200"
            : list.length
              ? "border-gray-200 hover:border-violet-200"
              : "cursor-not-allowed border-gray-100 opacity-60")
        }
        onClick={function () {
          if (list.length) props.onSetRisks("type", type);
        }}
      >
        <span className="flex items-start justify-between gap-2 text-xs font-semibold text-gray-800">
          <span>{riskTypeLabel(type)}</span>
          <span className="group relative">
            <button
              type="button"
              className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[10px] font-bold text-gray-500"
              aria-label={"About " + riskTypeLabel(type)}
              onClick={function (e) {
                e.stopPropagation();
              }}
            >
              ?
            </button>
            <span className="pointer-events-none absolute right-0 z-10 hidden w-64 rounded-md border border-gray-200 bg-white p-2 text-left text-[11px] font-normal leading-snug text-gray-700 shadow-lg group-hover:block group-focus-within:block">
              <strong className="block text-gray-900">Definition</strong>
              {type === "single-owner"
                ? "A contact covered by exactly one current owner, with no backup."
                : type === "capacity"
                  ? "An owner whose current relationships exceed their limit, per account or portfolio-wide."
                  : "A contact covered by 3 or more current owners."}
              <strong className="mt-1 block text-gray-900">
                Best practice
              </strong>
              {type === "single-owner"
                ? "At least two owners for key roles. Start with key roles and contacts that carry an RL or a goal."
                : type === "capacity"
                  ? "Move lower-priority relationships to teammates under their limit, or correct the limit if it is wrong."
                  : "Reduce to two owners by stepping redundant owners back. Flagged for review, not as a failure."}
              <strong className="mt-1 block text-gray-900">
                If nothing changes
              </strong>
              {type === "single-owner"
                ? "If that owner leaves, the contact becomes an orphaned gap."
                : type === "capacity"
                  ? "Stretched owners let relationships go stale, and a departure hands many relationships over at once."
                  : "Redundant owners use capacity that could cover single-owner contacts."}
            </span>
          </span>
        </span>
        <span className="text-sm text-gray-900">
          <b className="text-xl">{n}</b> {unit}
        </span>
        {!list.length ? (
          <span className="text-[11px] text-gray-400">
            None match the current filters.
          </span>
        ) : null}
      </button>
    );
  };

  let body: React.ReactNode;
  if (!risksAll.length) {
    body = (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
        No risks{props.accountName ? " for " + props.accountName : ""}.
        {props.gapCount ? (
          <>
            {" "}
            <button
              type="button"
              className="font-medium text-[#974ae4] hover:underline"
              onClick={props.onSwitchToGaps}
            >
              {plural(props.gapCount, "gap")} → Gaps
            </button>
          </>
        ) : null}
      </div>
    );
  } else if (naRisks.groupBy === "type") {
    const types: RiskType[] =
      tab === "all" ? RISK_TYPES : ([tab] as RiskType[]);
    body = (
      <div className="flex flex-col gap-3">
        {types.map(function (type) {
          const list =
            type === "single-owner"
              ? model.single
              : type === "capacity"
                ? model.capacity
                : model.fan;
          if (!list.length) return null;
          return (
            <div
              key={type}
              className="rounded-lg border border-gray-200 bg-white"
              data-risk={type}
            >
              <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-900">
                {riskTypeLabel(type)}
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {type === "capacity"
                    ? plural(
                        new Set(list.map(i => i.owner_id).filter(Boolean)).size,
                        "owner"
                      )
                    : plural(list.length, "contact")}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-left text-xs">
                  <colgroup>
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>
                  <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Account</th>
                      <th className="px-3 py-2">Contact / owner</th>
                      <th className="px-3 py-2">Coverage now</th>
                      <th className="px-3 py-2">RL / goal</th>
                      <th className="px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(function (i) {
                      return (
                        <tr
                          key={i.id}
                          className={
                            "border-t border-gray-100 " +
                            (i.isKey ? "bg-amber-50/40" : "")
                          }
                        >
                          <td className="px-3 py-2">
                            {i.account_id ? (
                              <button
                                type="button"
                                className="font-medium text-[#974ae4] hover:underline"
                                onClick={function () {
                                  props.onAccount(i.account_id!);
                                }}
                              >
                                {i.account_name}
                              </button>
                            ) : (
                              <strong>Portfolio-wide</strong>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {type === "capacity" ? (
                              <>
                                <button
                                  type="button"
                                  className="font-medium text-[#974ae4] hover:underline"
                                  onClick={function () {
                                    if (i.owner_id)
                                      props.onOpenPortfolio(i.owner_id, "over");
                                  }}
                                >
                                  {i.owner_name}
                                </button>
                                <div className="text-[10px] text-gray-500">
                                  {i.owner_role}
                                </div>
                              </>
                            ) : (
                              <>
                                {i.contact_name}
                                <KeyBadge badge={i.keyBadge} />
                                <div className="text-[10px] text-gray-500">
                                  {i.contact_role_label}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {type === "capacity"
                              ? i.local_overload
                                ? i.local_fan_out +
                                  " / " +
                                  i.local_capacity +
                                  " in this account"
                                : i.global_fan_out +
                                  " / " +
                                  i.global_capacity +
                                  " portfolio-wide"
                              : type === "single-owner"
                                ? "Only " + (i.owner_name || "one owner")
                                : (i.current_owner_names || []).length +
                                  " owners: " +
                                  (i.current_owner_names || []).join(", ")}
                          </td>
                          <td className="px-3 py-2">
                            {type === "capacity" ? (
                              "—"
                            ) : (
                              <>
                                {i.rl != null ? "RL " + i.rl : "Not assessed"}
                                {i.goal_target_rl != null
                                  ? " · goal RL" + i.goal_target_rl
                                  : ""}
                              </>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {type === "capacity" && i.owner_id ? (
                              <NaCtaLink
                                onClick={function () {
                                  props.onOpenPortfolio(i.owner_id!, "over");
                                }}
                              >
                                Open portfolio →
                              </NaCtaLink>
                            ) : i.account_id && i.contact_id ? (
                              <NaCtaLink
                                onClick={function () {
                                  props.onAddOwner(
                                    i.account_id!,
                                    i.contact_id!
                                  );
                                }}
                              >
                                {type === "single-owner"
                                  ? "Add a second owner →"
                                  : "Review owners →"}
                              </NaCtaLink>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {type === "single-owner" ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 px-3 py-1.5">
                  {Array.from(
                    new Set(list.map(i => i.owner_id).filter(Boolean))
                  ).map(function (oid) {
                    const name =
                      list.find(i => i.owner_id === oid)?.owner_name || oid;
                    return (
                      <React.Fragment key={String(oid)}>
                        <NaCtaLink
                          onClick={function () {
                            props.onOpenPortfolio(String(oid), "only");
                          }}
                        >
                          Open {String(name).split(" ")[0]}&apos;s portfolio →
                        </NaCtaLink>
                        <NaCtaLink
                          disabled={!canManageTransfer}
                          onClick={function () {
                            props.onSimulate(String(oid));
                          }}
                        >
                          Simulate departure →
                        </NaCtaLink>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  } else {
    body = (
      <RisksGrouped
        items={props.risksShown}
        groupBy={naRisks.groupBy === "owner" ? "owner" : "account"}
        onAccount={props.onAccount}
        onAddOwner={props.onAddOwner}
        onOpenPortfolio={props.onOpenPortfolio}
      />
    );
  }

  const lead = model.ownerLine[0];
  const leadFirst = lead ? lead.name.split(" ")[0] : "";
  const portfolioNotes =
    props.selectedAccount === "all"
      ? []
      : props.risksBase.filter(function (item) {
          if (item.condition_key !== "capacity-global" || !item.owner_id)
            return false;
          return props.rows.some(function (row) {
            return (
              row.account_id === props.selectedAccount &&
              row.owner_id === item.owner_id &&
              row.lifecycle_state === "active"
            );
          });
        });

  return (
    <div className="flex flex-col gap-3">
      {portfolioNotes.map(function (item) {
        const here = props.rows.filter(function (row) {
          return (
            row.account_id === props.selectedAccount &&
            row.owner_id === item.owner_id &&
            row.lifecycle_state === "active"
          );
        }).length;
        return (
          <p
            key={item.id}
            className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600"
          >
            Also: {item.owner_name} is over the portfolio limit (
            {item.global_fan_out} / {item.global_capacity}) and owns{" "}
            {plural(here, "relationship")} in {props.accountName}. Not counted
            as a {props.accountName} risk.{" "}
            <button
              type="button"
              className="font-medium text-[#974ae4] hover:underline"
              onClick={function () {
                if (item.owner_id) props.onSeeOwnerRisks(item.owner_id);
              }}
            >
              See {item.owner_name}&apos;s workload →
            </button>
          </p>
        );
      })}
      {risksAll.length ? (
        <>
          <div
            className="grid gap-2 sm:grid-cols-3"
            role="group"
            aria-label="Risk types"
          >
            {RISK_TYPES.map(tile)}
          </div>
          {lead ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
              <span>
                {lead.types === model.patterns
                  ? "All " + model.patterns + " patterns"
                  : lead.types + " of " + model.patterns + " patterns"}{" "}
                involve <strong>{lead.name}</strong>
                {lead.single.length
                  ? ": if " +
                    leadFirst +
                    " leaves, " +
                    plural(lead.single.length, "contact") +
                    (lead.single.length === 1
                      ? " becomes a gap"
                      : " become gaps")
                  : ""}
              </span>
              <button
                type="button"
                className="ml-2 font-medium text-[#974ae4] hover:underline"
                onClick={function () {
                  props.onOpenPortfolio(lead.id, null);
                }}
              >
                Open portfolio
              </button>
              {lead.single.length ? (
                <button
                  type="button"
                  className={
                    "ml-2 font-medium " +
                    (canManageTransfer
                      ? "text-[#974ae4] hover:underline"
                      : "cursor-not-allowed text-gray-400")
                  }
                  disabled={!canManageTransfer}
                  title={
                    canManageTransfer ? undefined : "Need's Exec Ops Access"
                  }
                  onClick={function () {
                    if (canManageTransfer) props.onSimulate(lead.id);
                  }}
                >
                  Simulate departure
                </button>
              ) : null}
              {model.ownerLine.length > 1 ? (
                <span className="mt-1 block text-gray-500">
                  Also in several patterns:{" "}
                  {model.ownerLine.slice(1, 3).map(function (owner, idx) {
                    return (
                      <button
                        key={owner.id}
                        type="button"
                        className="font-medium text-[#974ae4] hover:underline"
                        onClick={function () {
                          props.onOpenPortfolio(owner.id, null);
                        }}
                      >
                        {idx ? ", " : ""}
                        {owner.name} ({owner.types})
                      </button>
                    );
                  })}
                </span>
              ) : null}
            </div>
          ) : null}
          {model.multi.length ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-900">
                Accounts with multiple risks
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {model.multi.length}
                </span>
                {props.selectedAccount !== "all" ? (
                  <button
                    type="button"
                    className="ml-2 text-xs font-medium text-[#974ae4] hover:underline"
                    onClick={function () {
                      props.onFilterAccount(null);
                    }}
                  >
                    × Show all
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ml-2 text-xs font-medium text-[#974ae4] hover:underline"
                  onClick={function () {
                    props.onSetRisks("groupBy", "account");
                    props.onSetRisks("type", "all");
                  }}
                >
                  See all by account →
                </button>
              </div>
              <table className="min-w-full text-left text-xs">
                <thead className="bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Account</th>
                    <th className="px-3 py-2">Single-owner</th>
                    <th className="px-3 py-2">Workload</th>
                    <th className="px-3 py-2">Fan-in</th>
                    <th className="px-3 py-2">Key-role contacts</th>
                    <th className="px-3 py-2">Common owner</th>
                  </tr>
                </thead>
                <tbody>
                  {model.multi.slice(0, 5).map(function (a) {
                    const on = props.selectedAccount === a.id;
                    return (
                      <tr
                        key={a.id}
                        className={
                          "cursor-pointer border-t border-gray-100 " +
                          (on ? "bg-violet-50" : "hover:bg-gray-50")
                        }
                        onClick={function () {
                          props.onFilterAccount(on ? null : a.id);
                        }}
                      >
                        <td className="px-3 py-2 font-medium">{a.name}</td>
                        <td className="px-3 py-2">
                          {a.counts["single-owner"] || "·"}
                        </td>
                        <td className="px-3 py-2">
                          {a.counts.capacity || "·"}
                        </td>
                        <td className="px-3 py-2">
                          {a.counts["fan-in"] || "·"}
                        </td>
                        <td className="px-3 py-2">{a.keyContacts}</td>
                        <td className="px-3 py-2">{a.commonOwner}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 pb-3">
        <Chip
          label="All risks"
          count={risksAll.length}
          active={tab === "all"}
          onClick={function () {
            props.onSetRisks("type", "all");
          }}
        />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-semibold text-gray-500">
            Group by
          </span>
          <DropdownSelector
            label="Group by"
            hideLabel
            className="na-inline-select"
            selected={naRisks.groupBy}
            options={[
              { value: "type", label: "Risk type" },
              { value: "owner", label: "Owner" },
              { value: "account", label: "Account" },
            ]}
            onSelect={function (v) {
              props.onSetRisks("groupBy", v);
            }}
            compact
            triggerStyle="field"
          />
        </div>
      </div>
      {body}
    </div>
  );
}

export default function NeedsAttentionPane(props: {
  attentionItems: AttentionItem[];
  accounts: CoverageAccount[];
  rows: CoverageRow[];
  filters: CoverageFilters;
  readinessSegment: string;
  readinessAccountIds: Set<string> | null;
  naView: NaView;
  naGaps: NaGapsState;
  naRisks: NaRisksState;
  naFocusLabel: string | null;
  meddpiccOn: boolean;
  onSetNaView: (view: NaView) => void;
  onSetGaps: (key: keyof NaGapsState, value: string) => void;
  onSetRisks: (key: keyof NaRisksState, value: string) => void;
  onClearFocus: () => void;
  onAccount: (accountId: string) => void;
  onSetup: (accountId: string) => void;
  onSuccessor: (ownerId: string) => void;
  onConfirm: (accountId: string) => void;
  onAddOwner: (accountId: string, contactId: string) => void;
  onOpenPortfolio: (ownerId: string, filter?: string | null) => void;
  onSimulate: (ownerId: string) => void;
  onFilterAccount: (accountId: string | null) => void;
  onSeeOwnerRisks: (ownerId: string) => void;
  onViewHistory: (accountId: string) => void;
}) {
  const gapsBase = useMemo(
    function () {
      return buildGapItems(props.attentionItems, {
        meddpiccOn: props.meddpiccOn,
      });
    },
    [props.attentionItems, props.meddpiccOn]
  );
  const risksBase = useMemo(
    function () {
      return buildRiskItems(props.attentionItems, {
        meddpiccOn: props.meddpiccOn,
      });
    },
    [props.attentionItems, props.meddpiccOn]
  );

  const matchOpts = {
    readinessAccountIds: props.readinessAccountIds,
  };
  const gapsAll = gapsBase.filter(i =>
    naSharedMatch(i, props.filters, matchOpts)
  );
  const risksAll = risksBase.filter(function (i) {
    if (!naSharedMatch(i, props.filters, matchOpts)) return false;
    if (
      props.filters.account !== "all" &&
      i.riskType === "capacity" &&
      i.global_overload &&
      !i.local_overload
    ) {
      return false;
    }
    return true;
  });
  const model = useMemo(
    function () {
      return buildRiskModel(risksAll);
    },
    [risksAll]
  );

  const gapTab = props.naGaps.type;
  const gapsShown = gapsAll.filter(function (i) {
    if (gapTab === "unowned+orphaned") {
      const ok =
        i.gapType === "unowned" ||
        (i.gapType === "lifecycle" && i.status === "open-orphaned");
      if (!ok) return false;
    } else if (gapTab !== "all" && gapTab.indexOf(",") >= 0) {
      if (gapTab.split(",").indexOf(i.gapType) < 0) return false;
    } else if (gapTab !== "all" && i.gapType !== gapTab) return false;
    if (!naAgeMatch(i, props.naGaps.age)) return false;
    if (props.naGaps.lifecycle === "orphaned" && i.gapType === "lifecycle") {
      return i.status === "open-orphaned";
    }
    if (props.naGaps.lifecycle === "covered" && i.gapType === "lifecycle") {
      return i.status === "open-covered";
    }
    return true;
  });

  const riskTab = props.naRisks.type;
  const risksShown =
    riskTab === "all" ? risksAll : risksAll.filter(i => i.riskType === riskTab);

  const forAcct =
    props.filters.account !== "all"
      ? " for " +
        (props.accounts.find(a => a.account_id === props.filters.account)
          ?.name || props.filters.account)
      : "";

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4"
      data-na-view={props.naView}
    >
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Needs attention</h3>
        <p className="text-xs text-gray-500">
          Gaps: coverage missing now · Risks: coverage that exists but is
          fragile
        </p>
      </div>

      {props.naFocusLabel ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-violet-50 px-3 py-2 text-xs text-violet-900">
          <span>Focus: {props.naFocusLabel}</span>
          <button
            type="button"
            className="font-medium text-[#974ae4] hover:underline"
            onClick={props.onClearFocus}
          >
            Clear focus ×
          </button>
        </div>
      ) : null}

      <TabsComponent
        tabs={[
          {
            id: "gaps",
            label: "Gaps · " + plural(gapsAll.length, "item") + forAcct,
          },
          {
            id: "risks",
            label: "Risks · " + plural(model.patterns, "pattern") + forAcct,
          },
        ]}
        activeTab={props.naView}
        onTabChange={function (id: NaView) {
          props.onSetNaView(id);
        }}
        variant="tab-underline"
        className="mb-4"
      />

      {props.naView === "gaps" ? (
        <GapsView
          gapsAll={gapsAll}
          gapsShown={gapsShown}
          rows={props.rows}
          naGaps={props.naGaps}
          riskPatterns={model.patterns}
          accountScope={forAcct}
          onSetGaps={props.onSetGaps}
          onSwitchToRisks={function () {
            props.onSetNaView("risks");
          }}
          onAccount={props.onAccount}
          onSetup={props.onSetup}
          onSuccessor={props.onSuccessor}
          onConfirm={props.onConfirm}
          onAddOwner={props.onAddOwner}
          onViewHistory={props.onViewHistory}
        />
      ) : (
        <RisksView
          risksAll={risksAll}
          risksShown={risksShown}
          model={model}
          naRisks={props.naRisks}
          gapCount={gapsAll.length}
          onSetRisks={props.onSetRisks}
          onSwitchToGaps={function () {
            props.onSetNaView("gaps");
          }}
          onAccount={props.onAccount}
          onAddOwner={props.onAddOwner}
          onOpenPortfolio={props.onOpenPortfolio}
          onSimulate={props.onSimulate}
          onFilterAccount={props.onFilterAccount}
          onSeeOwnerRisks={props.onSeeOwnerRisks}
          selectedAccount={props.filters.account}
          accountName={forAcct.replace(/^ for /, "")}
          risksBase={risksBase}
          rows={props.rows}
        />
      )}
    </div>
  );
}
